import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Autocomplete, TextField } from "@mui/material";
import { getTextFieldStyles, getAutocompleteStyles, getAutocompleteListboxStyles } from "../../styles/formStyles";
import CustomFormDialog from "../../components/CustomFormDialog/CustomFormDialog";
import CustomPopup from "../../components/CustomPopup/CustomPopup";
import Table, { Column } from "../../components/Table/Table";
import { Delete, Edit, Search } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useAlert } from "../../contexts/AlertContext";
import { useTranslation } from "react-i18next";
import { useWarehouse } from "../../contexts/WarehouseContext";
import { documentsApi, DocumentApiResponse } from "../../services/documentsApi";
import { useServerSidePagination } from "../../hooks/useServerSidePagination";
import { PaginatedResponse } from "../../types/api";
import { LabelChip, LabelSelector } from "../../components/Labels";
import { WarehouseSelector } from "../../components/WarehouseSelector";
import { ClientSelector } from "../../components/ClientSelector";
import { ProductSelector, ProductSelection } from "../../components/ProductSelector";

const Documents = () => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingDocument, setEditingDocument] = useState<DocumentApiResponse | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<DocumentApiResponse | null>(null);
    const [generatedDocumentNumber, setGeneratedDocumentNumber] = useState<string>('');
    const [isGeneratingNumber, setIsGeneratingNumber] = useState<boolean>(false);
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { t } = useTranslation();
    const { selectedWarehouse, loading: warehousesLoading } = useWarehouse();

    // Wrapper function to convert 0-indexed page to 1-indexed for API
    const fetchDocuments = useCallback(
        async (
            page: number,
            pageSize: number,
            search: string
        ): Promise<PaginatedResponse<DocumentApiResponse>> => {
            // Don't fetch documents until warehouse is loaded and selected
            if (warehousesLoading || !selectedWarehouse?.id) {
                return {
                    count: 0,
                    next: null,
                    previous: null,
                    results: [],
                };
            }

            const response = await documentsApi.getDocuments({
                page: page + 1, // Convert 0-indexed to 1-indexed for API
                page_size: pageSize,
                search: search || undefined,
                warehouse: selectedWarehouse.id,
            });
            return response;
        },
        [selectedWarehouse?.id, warehousesLoading]
    );

    const pagination = useServerSidePagination(fetchDocuments, {
        initialPageSize: 10,
        debounceMs: 500,
        onError: (error: any) => {
            const errorMessage =
                error.response?.data?.detail ||
                error.message ||
                "Błąd połączenia z backendem";
            showAlert("error", `Błąd podczas pobierania dokumentów: ${errorMessage}`, 5000);
            console.error("Błąd podczas pobierania dokumentów:", error);
        },
    });

    const columns: Column<DocumentApiResponse>[] = [
        {
            id: "document_number",
            label: t("documents.columns.name"),
            flex: 1.5,
        },
        {
            id: "document_type_display",
            label: t("documents.columns.type"),
            flex: 1,
        },
        {
            id: "document_status_display",
            label: t("documents.columns.status"),
            flex: 1,
            format: (value: string, row: DocumentApiResponse) => {
                const statusKey = row.document_status;
                const statusLabels: Record<string, string> = {
                    oczekujaca: "Oczekująca",
                    zweryfikowano: "Zweryfikowano",
                    nowy: "Nowy",
                    zrealizowany: "Zrealizowany",
                    w_realizacji: "W trakcie realizacji",
                    wyslana: "Wysłana",
                    dostarczona: "Dostarczona",
                    anulowany: "Anulowany",
                };
                return statusLabels[statusKey] || t(`documents.statuses.${statusKey}`) || value;
            },
        },
        {
            id: "document_date",
            label: t("documents.columns.uploadDate"),
            flex: 1,
            format: (value: string) => {
                if (!value) return "-";
                return new Date(value).toLocaleDateString();
            },
        },
        {
            id: "document_amount",
            label: t("documents.columns.amount"),
            flex: 1,
            format: (value: string) => {
                const numValue = parseFloat(value);
                return isNaN(numValue) ? "-" : `${numValue.toFixed(2)} ${t("common.currency")}`;
            },
        },
        {
            id: "labels" as keyof DocumentApiResponse,
            label: t("documents.columns.labels"),
            flex: 1.5,
            format: (value: any) => {
                if (!value || !Array.isArray(value) || value.length === 0) {
                    return "-";
                }
                return (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", maxWidth: 300 }}>
                        {value.map((label: any) => (
                            <LabelChip key={label.id} label={label} size="small" />
                        ))}
                    </Box>
                );
            },
        },
        {
            id: "id" as keyof DocumentApiResponse,
            label: t("common.actions"),
            flex: 1.2,
            align: "center",
            sortable: false,
            format: (_, row) => (
                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                    <IconButton
                        size="small"
                        color="primary"
                        title={t("common.details")}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/documents/${row.id}`);
                        }}
                    >
                        <Search fontSize="small" />
                    </IconButton>
                    {!['zrealizowany', 'dostarczona', 'anulowany', 'zweryfikowano', 'zatwierdzony'].includes(row.document_status) && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/documents/edit/${row.id}`);
                            }}
                            size="small"
                            color="primary"
                            title={t("common.edit")}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    )}
                    {!['zrealizowany', 'dostarczona', 'anulowany', 'zweryfikowano', 'zatwierdzony'].includes(row.document_status) && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDocument(row);
                                setDeleteDialogOpen(true);
                            }}
                            size="small"
                            color="primary"
                            title={t("common.delete")}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            ),
        },
    ];

    const handleSaveDocument = async (dialogFormData: any) => {
        try {
            // Merge dialog form data with labels and products from state
            const mergedData = {
                ...dialogFormData,
                label_ids: formData.label_ids || [],
                products: formData.products || [],
            };

            // Get location_id from dialogFormData (warehouse selector is now part of form)
            const locationId = mergedData.location_id || selectedWarehouse?.id || null;

            // Get client_id - can be number or null
            const clientId = mergedData.client_id !== null && mergedData.client_id !== undefined
                ? (typeof mergedData.client_id === 'number' ? mergedData.client_id : parseInt(String(mergedData.client_id)))
                : undefined;

            // Format products for API - always include unit_price
            // For type 'A', use user-entered price, for other types use product price from backend
            const formattedProducts = mergedData.products
                .filter((p: ProductSelection) => p.quantity > 0)
                .map((p: ProductSelection) => ({
                    product_id: p.product_id,
                    quantity: p.quantity,
                    unit_price: p.unit_price || '0.00',
                }));

            if (editingDocument) {
                // Update existing document
                await documentsApi.updateDocument(editingDocument.id, {
                    document_date: mergedData.document_date || undefined,
                    location_id: locationId || undefined,
                    client_id: clientId,
                    document_notes: mergedData.document_notes || undefined,
                    label_ids: mergedData.label_ids ?? [],
                    products: formattedProducts.length > 0 ? formattedProducts : undefined,
                });
                showAlert("success", t("documents.messages.updated") || "Dokument zaktualizowany");
            } else {
                // Create new document
                // NIE wysyłaj document_number - backend wygeneruje go automatycznie
                await documentsApi.createDocument({
                    document_type: mergedData.document_type,
                    document_date: mergedData.document_date || undefined,
                    // document_number: NIE WYSYŁAJ - backend generuje automatycznie
                    location_id: locationId || undefined,
                    client_id: clientId,
                    document_notes: mergedData.document_notes || undefined,
                    document_currency: mergedData.document_currency || "PLN",
                    // Always send labels array (empty = brak etykiet)
                    label_ids: mergedData.label_ids ?? [],
                    products: formattedProducts.length > 0 ? formattedProducts : undefined,
                });
                showAlert("success", t("documents.messages.created") || "Dokument utworzony");
            }
            pagination.refresh();
            setIsDialogOpen(false);
            setEditingDocument(null);
            setCurrentDocumentType('');
            setGeneratedDocumentNumber(''); // Resetuj wygenerowany numer po zapisie
        } catch (error: any) {
            const errorMessage =
                error.response?.data?.detail ||
                error.message ||
                "Błąd podczas zapisywania dokumentu";
            showAlert("error", errorMessage, 5000);
            console.error("Błąd podczas zapisywania dokumentu:", error);
        }
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setEditingDocument(null);
        setCurrentDocumentType('');
        setGeneratedDocumentNumber(''); // Resetuj wygenerowany numer
        setFormData({
            label_ids: [],
            products: [],
            location_id: selectedWarehouse?.id || null,
        });
    };

    const documentTypeOptions = useMemo(() => [
        { value: 'A', label: 'Awizacja' },
        { value: 'WZ', label: 'Wydanie z Magazynu' },
        { value: 'KOR', label: 'Korekta stanu magazynowego' },
    ], []);

    const documentFormFields = useMemo(() => [
        {
            name: "document_type",
            label: t("documents.form.documentType"),
            required: true,
            customComponent: (props: {
                value: any;
                onChange: (value: any) => void;
                error?: boolean;
                helperText?: string;
                label: string;
                required?: boolean;
                disabled?: boolean;
            }) => {
                const selectedOption = documentTypeOptions.find(opt => opt.value === props.value) || null;
                return (
                    <Autocomplete
                        options={documentTypeOptions}
                        value={selectedOption}
                        onChange={(_event, newValue) => {
                            props.onChange(newValue ? newValue.value : null);
                        }}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        disabled={props.disabled}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={props.label}
                                error={props.error}
                                helperText={props.helperText}
                                required={props.required}
                                fullWidth
                                margin="normal"
                                sx={getTextFieldStyles({ error: props.error })}
                            />
                        )}
                        fullWidth
                        sx={getAutocompleteStyles()}
                        ListboxProps={{
                            sx: getAutocompleteListboxStyles(),
                        }}
                    />
                );
            },
        },
        {
            name: "document_date",
            label: t("documents.form.documentDate"),
            type: "date",
            required: false,
        },
        {
            name: "document_number",
            label: t("documents.form.documentNumber"),
            required: false,
            customComponent: (props: {
                value: any;
                onChange: (value: any) => void;
                error?: boolean;
                helperText?: string;
                label: string;
                required?: boolean;
                disabled?: boolean;
            }) => (
                <TextField
                    label={props.label}
                    value={editingDocument ? props.value : generatedDocumentNumber || ''}
                    onChange={(e) => {
                        // Tylko podczas edycji pozwól na ręczną zmianę
                        if (editingDocument) {
                            props.onChange(e.target.value);
                        }
                    }}
                    error={props.error}
                    helperText={
                        !editingDocument
                            ? (isGeneratingNumber
                                ? (t("documents.form.generatingNumber") || "Generowanie numeru...")
                                : (t("documents.form.documentNumberAutoGenerated") || "Numer zostanie wygenerowany automatycznie"))
                            : props.helperText
                    }
                    required={props.required}
                    disabled={props.disabled || !editingDocument || isGeneratingNumber}
                    fullWidth
                    margin="normal"
                    sx={{
                        ...getTextFieldStyles({ error: props.error }),
                        "& .MuiInputBase-input": {
                            color: "#ffffff",
                            WebkitTextFillColor: "#ffffff",
                        },
                        "& .MuiInputBase-input.Mui-disabled": {
                            color: "#ffffff",
                            WebkitTextFillColor: "#ffffff",
                            opacity: 1,
                        },
                        "& .MuiOutlinedInput-root": {
                            "& .MuiInputBase-input": {
                                color: "#ffffff",
                                WebkitTextFillColor: "#ffffff",
                            },
                        },
                        "& .MuiFormHelperText-root": {
                            color: "#ffffff !important",
                        },
                        "& .MuiFormHelperText-root.Mui-error": {
                            color: "#ffffff !important",
                        },
                        "& .MuiInputLabel-root": {
                            color: "#ffffff",
                            "&.Mui-focused": {
                                color: "#ffffff",
                            },
                        },
                    }}
                />
            ),
        },
        {
            name: "location_id",
            label: t("documents.form.location") || "Magazyn",
            required: false,
            customComponent: (props: {
                value: any;
                onChange: (value: any) => void;
                error?: boolean;
                helperText?: string;
                label: string;
                required?: boolean;
                disabled?: boolean;
            }) => (
                <WarehouseSelector
                    value={props.value || selectedWarehouse?.id || null}
                    onChange={props.onChange}
                    error={props.error}
                    helperText={props.helperText}
                    label={props.label}
                    required={props.required}
                />
            ),
        },
        {
            name: "client_id",
            label: t("documents.form.client"),
            required: false,
            customComponent: (props: {
                value: any;
                onChange: (value: any) => void;
                error?: boolean;
                helperText?: string;
                label: string;
                required?: boolean;
                disabled?: boolean;
            }) => (
                <ClientSelector
                    value={props.value || null}
                    onChange={props.onChange}
                    error={props.error}
                    helperText={props.helperText}
                    label={props.label}
                    required={props.required}
                />
            ),
        },
        {
            name: "document_notes",
            label: t("documents.form.notes"),
            required: false,
            multiline: true,
            rows: 3,
        },
        {
            name: "document_currency",
            label: t("documents.form.currency"),
            required: false,
        },
    ], [t, selectedWarehouse?.id, editingDocument, generatedDocumentNumber, isGeneratingNumber, documentTypeOptions]);

    const validateDocument = (data: any) => {
        const errors: Record<string, string> = {};
        if (!data.document_type) {
            errors.document_type = t("documents.validation.documentTypeRequired");
        }
        return errors;
    };

    // Form data state for labels, products, warehouse, and document type
    const [formData, setFormData] = useState<any>({
        label_ids: [],
        products: [],
        location_id: selectedWarehouse?.id || null,
    });
    const [currentDocumentType, setCurrentDocumentType] = useState<string>('');

    // Function to generate document number
    const generateDocumentNumber = useCallback(async (documentType: string | null, locationId: number | null) => {
        // Resetuj numer jeśli brakuje wymaganych danych
        if (!documentType || !locationId) {
            setGeneratedDocumentNumber('');
            return;
        }

        setIsGeneratingNumber(true);
        try {
            const number = await documentsApi.generateDocumentNumber(documentType, locationId);
            setGeneratedDocumentNumber(number);
        } catch (error: any) {
            console.error('Błąd podczas generowania numeru dokumentu:', error);
            setGeneratedDocumentNumber('');
            // Nie pokazuj błędu użytkownikowi - numer zostanie wygenerowany przy zapisie
        } finally {
            setIsGeneratingNumber(false);
        }
    }, []);

    // Update formData when selectedWarehouse changes
    useEffect(() => {
        if (selectedWarehouse?.id && !editingDocument) {
            setFormData((prev: any) => ({
                ...prev,
                location_id: selectedWarehouse.id
            }));
        }
    }, [selectedWarehouse?.id, editingDocument]);

    // Generate document number when dialog opens with both document_type and location_id
    useEffect(() => {
        if (isDialogOpen && !editingDocument && currentDocumentType && (selectedWarehouse?.id || formData.location_id)) {
            const locationId = formData.location_id || selectedWarehouse?.id || null;
            if (locationId) {
                generateDocumentNumber(currentDocumentType, locationId);
            }
        }
    }, [isDialogOpen, editingDocument, currentDocumentType, selectedWarehouse?.id, formData.location_id, generateDocumentNumber]);

    // Custom content for labels and products selectors
    const customFormContent = useMemo(() => {
        const showPrice = false;
        return (
            <Box>
                <LabelSelector
                    value={formData.label_ids || []}
                    onChange={(labelIds: number[]) => {
                        setFormData((prev: any) => ({ ...prev, label_ids: labelIds }));
                    }}
                    label={t("documents.form.labels")}
                />
                {currentDocumentType && (
                    <ProductSelector
                        value={formData.products || []}
                        onChange={(products: ProductSelection[]) => {
                            setFormData((prev: any) => ({ ...prev, products }));
                        }}
                        showPrice={showPrice}
                        label={t("documents.form.products")}
                    />
                )}
            </Box>
        );
    }, [formData.label_ids, formData.products, currentDocumentType, t]);

    // Refresh data when warehouse changes (but only after warehouses are loaded)
    useEffect(() => {
        if (!warehousesLoading && selectedWarehouse?.id !== undefined) {
            // Reset to first page and refresh data
            if (pagination.page !== 0) {
                pagination.handlePageChange(0);
            } else {
                pagination.refresh();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWarehouse?.id, warehousesLoading]);

    return (
        <Box>
            <Table
                columns={columns}
                data={pagination.data}
                page={pagination.page}
                rowsPerPage={pagination.pageSize}
                totalRows={pagination.totalCount}
                onPageChange={pagination.handlePageChange}
                onRowsPerPageChange={pagination.handlePageSizeChange}
                rowKey="id"
                checkboxSelection={false}
                searchable={true}
                title={t("documents.title")}
                description={t("documents.description")}
                searchPlaceholder={t("documents.searchPlaceholder")}
                onSearchChange={pagination.handleSearchChange}
                onAddButtonClick={() => {
                    navigate('/documents/create');
                }}
                addButtonText={t("documents.addDocument")}
                loading={pagination.isLoading}
            />

            <CustomFormDialog
                open={isDialogOpen}
                onClose={handleDialogClose}
                onSubmit={handleSaveDocument}
                title={
                    editingDocument
                        ? t("documents.editDocument")
                        : t("documents.addNewDocument")
                }
                submitButtonText={
                    editingDocument ? t("common.saveChanges") : t("common.add")
                }
                cancelButtonText={t("common.cancel")}
                formFields={documentFormFields}
                initialData={
                    editingDocument
                        ? {
                            document_type: editingDocument.document_type || "",
                            document_date: editingDocument.document_date || "",
                            document_number: editingDocument.document_number || "",
                            location_id: editingDocument.location || selectedWarehouse?.id || null,
                            client_id: editingDocument.client || null,
                            document_notes: editingDocument.document_notes || "",
                            document_currency: editingDocument.document_currency || "PLN",
                        }
                        : {
                            document_type: "",
                            document_date: new Date().toISOString().split("T")[0],
                            document_number: "",
                            location_id: selectedWarehouse?.id || null,
                            client_id: null,
                            document_notes: "",
                            document_currency: "PLN",
                        }
                }
                validate={validateDocument}
                customContent={customFormContent}
                onFormDataChange={(data: any) => {
                    if (data.document_type !== undefined) {
                        setCurrentDocumentType(data.document_type || '');
                    }

                    // Generuj numer gdy zmienia się typ dokumentu lub magazyn
                    if (!editingDocument && (data.document_type !== undefined || data.location_id !== undefined)) {
                        const documentType = data.document_type !== undefined ? data.document_type : currentDocumentType;
                        const locationId = data.location_id !== undefined ? data.location_id : (selectedWarehouse?.id || null);

                        if (documentType && locationId) {
                            generateDocumentNumber(documentType, locationId);
                        } else {
                            setGeneratedDocumentNumber('');
                        }
                    }
                }}
            />

            <CustomPopup
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                title={t("documents.deleteConfirmation")}
                text={t("documents.deleteWarning")}
                confirmText={t("common.delete")}
                cancelText={t("common.cancel")}
                onConfirm={async () => {
                    if (selectedDocument) {
                        try {
                            await documentsApi.deleteDocument(selectedDocument.id);
                            showAlert("success", t("documents.messages.deleted"));
                            pagination.refresh();
                        } catch (error: any) {
                            const errorMessage =
                                error.response?.data?.detail ||
                                error.message ||
                                "Błąd podczas usuwania dokumentu";
                            showAlert("error", errorMessage, 5000);
                            console.error("Błąd podczas usuwania dokumentu:", error);
                        }
                    }
                    setDeleteDialogOpen(false);
                    setSelectedDocument(null);
                }}
            />
        </Box>
    );
};

export default Documents;
